import React from "react";
import { motion, useTransform } from "framer-motion";
import { graphql, useStaticQuery } from "gatsby";
import { useTranslation } from "react-i18next";
import { bgSize } from "@utils/keyframes";
import styled from "styled-components";
import tw from "twin.macro";
import { rawName } from "@utils/helpers";

import PortableText from "@components/portableText";

type QuoteProps = {
  inView: boolean,
  width: number,
  scroll: any,
  name: string
}

const Wrapper = tw(motion.div)`md:w-1/2 lg:relative lg:pl-80 lg:pr-30`;

const Mark = tw.div`text-2xl text-orange leading-40 -tracking-024 lg:leading-60`;

const OpinionShort = styled.div<{ wider: boolean }>`
  ${tw`
    mb-32 mr-10 text-xl leading-40 -tracking-016
    lg:text-2xl lg:leading-60 lg:mb-0 lg:-tracking-024
    xl:whitespace-pre-line
  `}
  ${({ wider }) => wider && tw`xl:relative xl:-left-50 xl:w-530`}
  
  span {
    ${tw`font-normal bg-no-repeat`}
    background-image: linear-gradient(120deg, #FEE4E2 0%, #FEE4E2 100%);
    background-position: 0 100%;
    background-size: 100% 0;
  }
  
  &.active span {
    animation: ${bgSize} .4s .4s forwards;
  }
`;

const CaseTestimonialQuote = ({ inView, width, scroll, name }: QuoteProps) => {
  const { t } = useTranslation();
  const Ref = React.useRef();

  const [sectionStart, setSectionStart] = React.useState(0);

  const opacity = useTransform(scroll, [sectionStart + 80, sectionStart + 380], [0, 1]);
  const top = useTransform(scroll, [sectionStart + 80, sectionStart + 380], [120, 0]);

  const { allSanityCases } = useStaticQuery(graphql`
    query {
      allSanityCases {
        nodes {
          slug { current }
          testimonials {
            testimonial { _rawEn, _rawPl}
          }
        }
      }
    }
  `);
  const data = allSanityCases.nodes.find(item => item.slug.current === name).testimonials;

  React.useLayoutEffect(() => {
    if (!Ref.current) return;

    const offsetStart = window && Ref.current.getClientRects()[0].top + window.pageYOffset - window.innerHeight;

    setSectionStart(offsetStart);
  });

  return (
    <Wrapper ref={Ref} style={ width >= 1024 && { top, opacity }}>
      <Mark>
        {t(`Testimonials:Mark`)}
      </Mark>
      <OpinionShort wider={ name === 'eurobank' } className={ inView && 'active' }>
        <PortableText blocks={data.testimonial[rawName]} />
      </OpinionShort>
    </Wrapper>
  );
};

export default CaseTestimonialQuote;
